import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Snackbar
} from '@mui/material';
import {
  Add,
  Map,
  Analytics,
  CameraAlt,
  CompareArrows,
  MoreVert,
  Delete,
  CheckCircle,
  Timeline,
  ClearAll,
  Refresh,
  BarChart
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useBusiness } from '../../contexts/BusinessContext';
import LocationMap from './LocationMap';
import KeywordSidebar from './KeywordSidebar';
import ComparisonMode from './ComparisonMode';
import RankingProgressOverlay from './RankingProgressOverlay';
import RankingTimeSeries from './RankingTimeSeries';

const RankingDashboard = () => {
  const { locationId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedBusiness } = useBusiness();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [progressKeyword, setProgressKeyword] = useState('');
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [snapshotSuccess, setSnapshotSuccess] = useState(false);
  const [keywordDialog, setKeywordDialog] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [rightPanelView, setRightPanelView] = useState('map'); // map, comparison, timeseries
  const [rankingHistory, setRankingHistory] = useState([]);
  const [simulationMenuAnchor, setSimulationMenuAnchor] = useState(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [karteSnapshotDate, setKarteSnapshotDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingAllVolumes, setRefreshingAllVolumesState] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [calculatingVolume, setCalculatingVolume] = useState({});

  // Refresh handler - clears cache and reloads all data
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Clear localStorage cache
      const cacheKey = `rankingOverview_${locationId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_time`);

      // Reload all data with forced refresh
      await loadRankingOverview(true);
      await loadKeywordSuggestions();
      await loadRankingHistory();

      console.log('✅ Ranking data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing ranking data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [locationId]);

  // 🔄 AUTO-REFRESH: Clear cache and load fresh data on page load
  useEffect(() => {
    if (locationId && locationId !== 'undefined') {
      console.log('🔄 AUTO-REFRESH: Clearing cache and loading fresh data...');
      // Clear localStorage cache on component mount
      const cacheKey = `rankingOverview_${locationId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_time`);

      // Load fresh data
      loadRankingOverview(true); // Force refresh
      loadKeywordSuggestions();
      loadRankingHistory();
    }
  }, [locationId]);

  // 🚀 NEW: React to business selection changes from dropdown
  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.locationId || selectedBusiness.id;

      if (businessLocationId === locationId) {
        console.log('🔄 Business context updated in Rankings, ensuring data is fresh:', {
          businessName: selectedBusiness.businessName,
          locationId: businessLocationId
        });

        // Reload all data when business changes
        loadRankingOverview();
        loadRankingHistory();
        loadKeywordSuggestions();
      }
    }
  }, [selectedBusiness, locationId]);

  // Auto-select first keyword when overview changes (only if no keyword is selected)
  useEffect(() => {
    if (overview && overview.trackedKeywords.length > 0) {
      // Check if keyword is specified in URL
      const keywordFromUrl = searchParams.get('keyword');
      if (keywordFromUrl) {
        // Try to find the keyword in trackedKeywords
        const foundKeyword = overview.trackedKeywords.find(k => k.keyword === decodeURIComponent(keywordFromUrl));
        if (foundKeyword) {
          setSelectedKeyword(foundKeyword.keyword);
          console.log('🔄 Selected keyword from URL:', foundKeyword.keyword);
          return;
        }
      }

      // Only auto-select the first keyword if:
      // 1. No keyword is currently selected, OR
      // 2. The currently selected keyword no longer exists in the overview
      if (!selectedKeyword || !overview.trackedKeywords.find(k => k.keyword === selectedKeyword)) {
        setSelectedKeyword(overview.trackedKeywords[0].keyword);
        console.log('🔄 Auto-selected first keyword:', overview.trackedKeywords[0].keyword);
      }
    }
  }, [overview, searchParams]);

  const loadRankingOverview = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Check localStorage cache (1 hour TTL) if not forcing refresh
      if (!forceRefresh) {
        const cacheKey = `rankingOverview_${locationId}`;
        const cached = localStorage.getItem(cacheKey);
        const cachedTime = localStorage.getItem(`${cacheKey}_time`);
        const ONE_HOUR = 60 * 60 * 1000;

        if (cached && cachedTime && Date.now() - parseInt(cachedTime) < ONE_HOUR) {
          setOverview(JSON.parse(cached));
          setLoading(false);
          return;
        }
      } else {
        // Clear cache when forcing refresh
        const cacheKey = `rankingOverview_${locationId}`;
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
      }

      const response = await axios.get(`/api/businesses/${locationId}/ranking-overview`);
      if (response.data.success) {
        setOverview(response.data.data);
        // Cache in localStorage for 1 hour
        const cacheKey = `rankingOverview_${locationId}`;
        localStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        localStorage.setItem(`${cacheKey}_time`, Date.now().toString());

        // 🔄 AUTO-IMPORT: Log successful loading of potentially auto-imported business
        if (response.data.data && response.data.data.businessName) {
          console.log(`✅ RANKING: Successfully loaded data for ${response.data.data.businessName}`);
          console.log(`📊 KEYWORDS WITH LOCAL VOLUME:`, response.data.data.trackedKeywords?.map(k => ({
            keyword: k.keyword,
            hasLocalVolume: !!k.localVolume,
            localVolumeLength: Array.isArray(k.localVolume) ? k.localVolume.length : 'N/A'
          })));
        }
      }
    } catch (error) {
      console.error('Error loading ranking overview:', error);

      // 🔄 AUTO-IMPORT: Enhanced error handling for new businesses
      if (error.response?.status === 404) {
        console.log('📝 RANKING: Business not found - auto-import should have been attempted');
        // Note: Auto-import happens in backend, so we just log here
      }
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const loadKeywordSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(`/api/businesses/${locationId}/keyword-suggestions`);
      if (response.data.success) {
        // Handle new structure with categorized suggestions
        const suggestions = response.data.data.suggestions || response.data.data;
        setKeywordSuggestions(Array.isArray(suggestions) ? suggestions : []);
      }
    } catch (error) {
      console.error('Error loading keyword suggestions:', error);
    }
  }, [locationId]);

  const loadRankingHistory = useCallback(async () => {
    try {
      console.log(`🔍 RANKING HISTORY: Loading for location ${locationId}`);
      const response = await axios.get(`/api/businesses/${locationId}/ranking-history`);
      
      console.log('📡 RANKING HISTORY API Response:', {
        success: response.data.success,
        dataStructure: Object.keys(response.data),
        hasSnapshots: !!(response.data.data?.snapshots),
        hasKeywordHistory: !!(response.data.data?.keywordHistory),
        snapshotsLength: response.data.data?.snapshots?.length || 0
      });
      
      if (response.data.success) {
        // CRITICAL FIX: Transform keywordHistory to snapshots format for comparison mode
        let snapshots = response.data.data?.snapshots || response.data.snapshots || [];
        
        // If no snapshots but keywordHistory exists, transform it
        if (snapshots.length === 0 && response.data.data?.keywordHistory) {
          console.log('🔧 RANKING HISTORY: Converting keywordHistory to snapshots format');
          const keywordHistory = response.data.data.keywordHistory;
          
          // Create a map to group entries by date
          const dateGroups = {};
          
          Object.keys(keywordHistory).forEach(keyword => {
            keywordHistory[keyword].forEach(entry => {
              // Use date string without time to group by day
              const dateKey = entry.date.split('T')[0]; // "2025-08-19" format
              
              if (!dateGroups[dateKey]) {
                dateGroups[dateKey] = {
                  snapshotDate: entry.date, // Keep original full date
                  analyzedAt: entry.date, // For compatibility
                  keywords: [],
                  isSimulation: false
                };
              }
              
              // Transform entry to keyword format expected by SplitMapComparison
              const keywordData = {
                keyword: keyword,
                averageRank: entry.averageRank,
                testLocations: entry.locations.map(loc => ({
                  locationName: loc.locationName,
                  lat: loc.coordinates?.lat,
                  lng: loc.coordinates?.lng,
                  rank: loc.rank
                }))
              };
              
              dateGroups[dateKey].keywords.push(keywordData);
            });
          });
          
          console.log('🔧 RANKING HISTORY: Date groups created:', Object.keys(dateGroups));
          
          // Convert to array and sort by date
          snapshots = Object.values(dateGroups).sort((a, b) => 
            new Date(a.snapshotDate) - new Date(b.snapshotDate)
          );
          
          console.log(`🔧 RANKING HISTORY: Converted ${Object.keys(keywordHistory).length} keywords into ${snapshots.length} snapshots`);
        }
        
        console.log(`✅ RANKING HISTORY: Loaded ${snapshots.length} snapshots`);
        setRankingHistory(snapshots);
        console.log('---snapshots',snapshots)
      }
    } catch (error) {
      console.error('❌ Error loading ranking history:', error);
      console.error('Response data:', error.response?.data);
    }
  }, [locationId]);

  const analyzeKeyword = async (keyword) => {
    try {
      setAnalyzing(true);
      setProgressKeyword(keyword);
      setProgressData({ phase: 'starting', currentPin: 0, totalPins: 18, message: 'Initialisiere...' });
      
      const response = await axios.post(`/api/businesses/${locationId}/analyze-keyword`, {
        keyword,
        priority: 'secondary'
      });
      
      if (response.data.success) {
        console.log('✅ Keyword added successfully, reloading data...');

        // Show toast notification
        setToast({
          open: true,
          message: `Keyword "${keyword}" hinzugefügt`,
          type: 'success'
        });

        // Reload overview with forceRefresh=true to bypass cache and call handleRefresh
        await handleRefresh();

        // Auto-select the analyzed keyword for map display
        setSelectedKeyword(keyword);
        setKeywordDialog(false);
        setNewKeyword('');
      } else {
        // Handle unsuccessful response
        console.error('Keyword analysis failed:', response.data);
        alert('Keyword-Analyse fehlgeschlagen. Versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Error during keyword analysis:', error);
      
      // Enhanced error handling with better user messages
      let errorMessage = 'Keyword-Analyse fehlgeschlagen';
      
      if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server-Fehler. Bitte versuchen Sie es später erneut.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Netzwerk-Fehler. Überprüfen Sie Ihre Internetverbindung.';
      }
      
      alert(errorMessage);
    } finally {
      setAnalyzing(false);
      setProgressData(null);
    }
  };

  

  const createSnapshot = async () => {
    if (!selectedKeyword) {
      alert('Bitte wählen Sie zuerst ein Keyword aus.');
      return;
    }

    setCreatingSnapshot(true);
    setSnapshotSuccess(false);
    
    try {
      console.log(`📸 Creating snapshot for keyword: ${selectedKeyword}`);
      
      const response = await axios.post(`/api/businesses/${locationId}/create-snapshot`, {
        notes: `Neue Messung für "${selectedKeyword}"`,
        selectedKeyword: selectedKeyword
      });
      
      console.log('📊 Snapshot API Response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Snapshot creation successful');
        
        // Show success state immediately
        setSnapshotSuccess(true);
        
        // Refresh all data to show updated snapshots
        console.log('🔄 Refreshing UI data...');
        await Promise.all([
          loadRankingOverview(),
          loadRankingHistory()
        ]);
        
        console.log('✅ UI data refreshed');
        
        // Show success message with snapshot info
        const totalSnapshots = response.data.data?.totalSnapshots || 'Unknown';
        const cacheCleared = response.data.data?.cacheCleared;
        
        // Zeige Validierungswarnungen an, falls vorhanden
        const warnings = response.data.data?.snapshot?.validationWarnings || response.data.data?.validationWarnings || [];
        if (warnings.length > 0) {
          alert(`✅ Messung erfolgreich erstellt!\n📊 Total Snapshots: ${totalSnapshots}\n${cacheCleared ? '🔄 Cache aktualisiert' : ''}\n\nHinweise:\n${warnings.join('\n')}`);
        } else {
          alert(`✅ Ranking-Messung für "${selectedKeyword}" erfolgreich erstellt!\n📊 Total Snapshots: ${totalSnapshots}${cacheCleared ? '\n🔄 Cache aktualisiert' : ''}`);
        }
        
        // Keep success icon visible for 3 seconds
        setTimeout(() => {
          setSnapshotSuccess(false);
        }, 3000);
        
      } else {
        // API returned but success was false
        console.error('Snapshot creation failed:', response.data);
        setSnapshotSuccess(false);
        alert(`❌ Fehler beim Durchführen der Messung: ${response.data.error || 'Unbekannter Fehler'}`);
      }
      
    } catch (error) {
      console.error('Error creating snapshot:', error);
      setSnapshotSuccess(false);
      
      // More detailed error information
      if (error.response) {
        console.error('API Error Details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        
        if (error.response.status === 500) {
          // Check if snapshot was still created despite error
          const errorMessage = error.response.data?.error || 'Interner Serverfehler';
          console.log('🔍 Checking if snapshot was created despite 500 error...');
          
          // Refresh data to check if snapshot exists
          try {
            await loadRankingHistory();
            alert(`⚠️ Server-Fehler aufgetreten, aber Snapshot möglicherweise trotzdem erstellt.\nFehler: ${errorMessage}\n\n✅ Daten wurden aktualisiert. Prüfen Sie die Snapshot-Liste.`);
          } catch (refreshError) {
            alert(`❌ Server-Fehler (500): ${errorMessage}`);
          }
        } else if (error.response.status === 404) {
          alert('❌ Business-Profil nicht gefunden (404)');
        } else if (error.response.status === 401) {
          alert('❌ Authentifizierung fehlgeschlagen (401)');
        } else {
          alert(`❌ Fehler beim Durchführen der Messung (${error.response.status}): ${error.response.data?.error || error.message}`);
        }
      } else if (error.request) {
        alert('❌ Netzwerkfehler: Server nicht erreichbar');
      } else {
        alert(`❌ Fehler beim Durchführen der Messung: ${error.message}`);
      }
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const deleteKeyword = async (keyword) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie das Keyword "${keyword}" löschen möchten?`)) {
      return;
    }

    try {
      const response = await axios.delete(`/api/businesses/${locationId}/keywords/${encodeURIComponent(keyword)}`);

      if (response.data.success) {
        // Show toast notification
        setToast({
          open: true,
          message: `Keyword "${keyword}" gelöscht`,
          type: 'success'
        });

        // Call handleRefresh to reload all data
        await handleRefresh();

        // Wenn gelöschtes Keyword ausgewählt war, wähle ein anderes
        if (selectedKeyword === keyword) {
          setSelectedKeyword(null);
        }
      }
    } catch (error) {
      console.error('Error deleting keyword:', error);
      alert('Fehler beim Löschen des Keywords.');
    }
  };

  const handleKeywordSelect = (keyword) => {
    setSelectedKeyword(keyword);
    // Will be auto-set to latest when karteAvailableDates updates
  };

  const handleKeywordRefresh = (keyword) => {
    analyzeKeyword(keyword);
  };

  const handleCalculateLocalVolume = async (keyword) => {
    if (!locationId || !selectedBusiness) {
      alert('Business-Daten nicht verfügbar. Bitte aktualisieren Sie die Seite.');
      return;
    }

    setCalculatingVolume(prev => ({ ...prev, [keyword]: true }));

    try {
      console.log(`📊 Calculating local volume for keyword: ${keyword}`);

      const response = await axios.post(`/api/businesses/${locationId}/calculate-local-volume`, {
        keyword
      });

      if (response.data.success) {
        console.log('✅ Local volume calculated and saved:', response.data.data);

        // Show success toast
        const localVol = response.data.data.localVolume;
        setToast({
          open: true,
          message: `Local Volume: ${localVol.localVolume.toLocaleString('de-DE')} searches/month (${(localVol.populationRatio * 100).toFixed(2)}% of national)`,
          type: 'success'
        });

        // Clear cache and reload overview to show updated keyword data
        console.log('🔄 Clearing cache and reloading ranking overview...');
        const cacheKey = `rankingOverview_${locationId}`;
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);

        // Reload with a small delay to ensure DB consistency
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadRankingOverview(true);

        console.log('✅ Data refreshed successfully');
      } else {
        console.error('Local volume calculation failed:', response.data);
        setToast({
          open: true,
          message: `Error: ${response.data.error || 'Local volume calculation failed'}`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error calculating local volume:', error);

      let errorMessage = 'Local volume calculation failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setToast({
        open: true,
        message: `Error: ${errorMessage}`,
        type: 'error'
      });
    } finally {
      setCalculatingVolume(prev => ({ ...prev, [keyword]: false }));
    }
  };

  const handleRefreshAllVolumes = async () => {
    if (!overview || !overview.trackedKeywords) {
      setToast({
        open: true,
        message: 'Keine Keywords gefunden',
        type: 'error'
      });
      return;
    }

    console.log(`📊 Refreshing local volumes for ${overview.trackedKeywords.length} keywords...`);
    let successCount = 0;
    let errorCount = 0;

    // Show progress overlay
    setProgressData({
      phase: 'running',
      progress: 0,
      total: overview.trackedKeywords.length,
      message: `Berechne lokale Volumen für ${overview.trackedKeywords.length} Keywords...`
    });

    for (let index = 0; index < overview.trackedKeywords.length; index++) {
      const keyword = overview.trackedKeywords[index];

      try {
        // Update progress
        setProgressKeyword(keyword.keyword);
        setProgressData({
          phase: 'running',
          progress: index + 1,
          total: overview.trackedKeywords.length,
          message: `Berechne: ${keyword.keyword}`
        });

        console.log(`📊 Calculating volume for: ${keyword.keyword}`);
        const response = await axios.post(`/api/businesses/${locationId}/calculate-local-volume`, {
          keyword: keyword.keyword
        });

        if (response.data.success) {
          successCount++;
          console.log(`✅ ${keyword.keyword} completed`);
        } else {
          errorCount++;
          console.warn(`⚠️ ${keyword.keyword} failed:`, response.data.error);
        }
      } catch (error) {
        errorCount++;
        console.error(`❌ Error for ${keyword.keyword}:`, error.message);
      }

      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Refresh data after all calculations
    console.log('🔄 Clearing cache and reloading...');
    const cacheKey = `rankingOverview_${locationId}`;
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(`${cacheKey}_time`);

    await new Promise(resolve => setTimeout(resolve, 500));
    await loadRankingOverview(true);

    // Complete progress
    setProgressData({
      phase: 'completed',
      progress: overview.trackedKeywords.length,
      total: overview.trackedKeywords.length,
      message: `✅ ${successCount} Keywords berechnet${errorCount > 0 ? `, ${errorCount} Fehler` : ''}`
    });

    setToast({
      open: true,
      message: `✅ ${successCount} Keywords berechnet${errorCount > 0 ? `, ${errorCount} Fehler` : ''}`,
      type: errorCount > 0 ? 'warning' : 'success'
    });

    console.log(`✅ Batch volume refresh complete: ${successCount} success, ${errorCount} errors`);

    // Clear progress after 2 seconds
    setTimeout(() => {
      setProgressData(null);
      setProgressKeyword('');
    }, 2000);
  };

  const generateSimulation = async () => {
    if (!window.confirm('Möchten Sie 5 Wochen historische Ranking-Daten simulieren? Dies erstellt Testdaten mit verbesserlichen Trends.')) {
      return;
    }

    try {
      setAnalyzing(true);
      const response = await axios.post(`/api/businesses/${locationId}/simulate-ranking-history`, {
        weeks: 5,
        improvementRate: 0.15 // 15% Verbesserungsrate
      });
      
      if (response.data.success) {
        await loadRankingOverview();
        await loadRankingHistory();
        alert(`✅ ${response.data.data.simulatedSnapshots} Wochen Simulations-Daten generiert! Prüfen Sie jetzt den Vergleichsmodus.`);
      }
    } catch (error) {
      console.error('Error generating simulation:', error);
      alert('Fehler beim Generieren der Simulation.');
    } finally {
      setAnalyzing(false);
    }
  };
// refershes all keyword analysis
const refreshAllKeywords = async () => {
    if (!window.confirm('Alle Keywords analysieren und Snapshot erstellen? Dies kann einige Minuten dauern.')) {
      return;
    }

    try {
      setAnalyzing(true);
      
      // Get all keywords for this business
      if (!overview?.trackedKeywords || overview.trackedKeywords.length === 0) {
        alert('Keine Keywords zum Analysieren vorhanden.');
        return;
      }

      const keywords = overview.trackedKeywords.map(k => k.keyword);
      
      // Analyze all keywords sequentially
      for (let i = 0; i < keywords.length; i++) {
        const keyword = keywords[i];
        setProgressKeyword(keyword);
        
        console.log(`🔍 Analyzing keyword ${i + 1}/${keywords.length}: ${keyword}`);
        
        const response = await axios.post(`/api/businesses/${locationId}/analyze-keyword`, {
          keyword: keyword,
          priority: 'secondary',
        
        });

        if (response.data.success) {
          console.log(`✅ Keyword ${keyword} analyzed successfully`);
        } else {
          console.error(`❌ Keyword ${keyword} analysis failed:`, response.data.error);
        }

        // Small delay between keyword analyses
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // Reload data after all analyses complete
      await loadRankingOverview();
      await loadRankingHistory();
      
      // Refresh cache to ensure latest snapshots are available
      try {
        const cacheResponse = await axios.post(`/api/businesses/${locationId}/refresh-cache`, {
          type: 'ranking-snapshots'
        });

        if (cacheResponse.data.success) {
          console.log('✅ Cache refreshed successfully');
        }
      } catch (cacheError) {
        console.error('❌ Error refreshing cache:', cacheError);
      }
      
      alert(`✅ Alle ${keywords.length} Keywords erfolgreich analysiert, Snapshot erstellt! und cache refreshed`);
      
    } catch (error) {
      console.error('Error refreshing all keywords:', error);
      alert('Fehler beim Analysieren der Keywords.');
    } finally {
      setAnalyzing(false);
      setProgressKeyword('');
    }
  };

  const clearSimulation = async () => {
    if (!window.confirm('Möchten Sie alle Simulations-Daten entfernen?')) {
      return;
    }

    try {
      setAnalyzing(true);
      const response = await axios.delete(`/api/businesses/${locationId}/clear-simulation`);
      
      if (response.data.success) {
        await loadRankingOverview();
        await loadRankingHistory();
        alert(`✅ ${response.data.data.removedSnapshots} Simulations-Snapshots entfernt.`);
      }
    } catch (error) {
      console.error('Error clearing simulation:', error);
      alert('Fehler beim Entfernen der Simulation.');
    } finally {
      setAnalyzing(false);
    }
  };

  const clearCache = async () => {
    if (!window.confirm('Cache für dieses Business löschen? Dies erzwingt das Neuladen aller Daten beim nächsten Aufruf.')) {
      return;
    }

    try {
      setClearingCache(true);
      console.log(`🧹 Clearing cache for business: ${locationId}`);
      
      const response = await axios.delete(`/api/businesses/${locationId}/clear-cache`);
      
      if (response.data.success) {
        console.log(`✅ Cache cleared: ${response.data.keysDeleted} keys deleted`);
        
        // Reload all data to show fresh results
        await Promise.all([
          loadRankingOverview(),
          loadRankingHistory(),
          loadKeywordSuggestions()
        ]);
        
        alert(`✅ Cache erfolgreich gelöscht!\n📊 ${response.data.keysDeleted} Cache-Einträge entfernt.\n🔄 Daten wurden neu geladen.`);
      } else {
        alert(`⚠️ ${response.data.message}\n${response.data.warning || ''}`);
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      
      let errorMessage = 'Fehler beim Löschen des Caches.';
      if (error.response?.status === 404) {
        errorMessage = 'Business nicht gefunden.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Nicht autorisiert. Bitte melden Sie sich erneut an.';
      } else if (error.response?.data?.error) {
        errorMessage = `Cache-Fehler: ${error.response.data.error}`;
      }
      
      alert(errorMessage);
    } finally {
      setClearingCache(false);
    }
  };

  const getSelectedKeywordData = () => {
    if (!selectedKeyword || !overview?.trackedKeywords) {
      return null;
    }
    return overview.trackedKeywords.find(k => k.keyword === selectedKeyword);
  };

  // Compute available snapshot dates for the Karte tab
  const karteAvailableDates = useMemo(() => {
    if (!selectedKeyword || !rankingHistory?.length) {
      return [];
    }

    // Group snapshots by day, keeping latest per day with the selected keyword
    const dayGroups = {};

    rankingHistory.forEach(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      const dateObj = new Date(dateValue);

      if (dateObj instanceof Date && !isNaN(dateObj)) {
        const dayKey = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD

        // Check if snapshot contains the selected keyword
        let keywordInSnapshot = entry.keywords?.find(k => k.keyword === selectedKeyword);
        let keywordInMetrics = entry.keywordMetrics?.find(k => k.keyword === selectedKeyword);

        // Fallback to case-insensitive match
        if (!keywordInSnapshot) {
          keywordInSnapshot = entry.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
        }
        if (!keywordInMetrics) {
          keywordInMetrics = entry.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
        }

        const hasKeyword = !!(keywordInSnapshot || keywordInMetrics);

        if (hasKeyword) {
          if (!dayGroups[dayKey]) {
            dayGroups[dayKey] = [];
          }
          dayGroups[dayKey].push({ dateValue, dateObj, entry });
        }
      }
    });

    // For each day, select the latest snapshot
    const dates = Object.keys(dayGroups)
      .map(dayKey => {
        const daySnapshots = dayGroups[dayKey].sort((a, b) => new Date(b.dateValue) - new Date(a.dateValue));
        const latest = daySnapshots[0];
        return {
          value: latest.dateValue,
          label: latest.dateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
          data: latest.entry
        };
      })
      .sort((a, b) => new Date(a.value) - new Date(b.value));

    return dates;
  }, [selectedKeyword, rankingHistory]);

  // Compute keyword data for Karte tab based on selected snapshot date
  const karteKeywordData = useMemo(() => {
    if (!selectedKeyword) {
      return null;
    }

    // If no snapshot date selected yet (initial state), use overview data
    if (!karteSnapshotDate) {
      if (!overview?.trackedKeywords) {
        return null;
      }
      const overviewKeyword = overview.trackedKeywords.find(k => k.keyword === selectedKeyword);
      // Ensure keyword data has testLocations for map display
      if (overviewKeyword) {
        return {
          ...overviewKeyword,
          testLocations: overviewKeyword.testLocations || []
        };
      }
      return null;
    }

    // Find snapshot by date and extract keyword data
    const selectedSnapshot = rankingHistory.find(entry => {
      const dateValue = entry.analyzedAt || entry.snapshotDate;
      return new Date(dateValue).getTime() === new Date(karteSnapshotDate).getTime();
    });

    if (!selectedSnapshot) {
      return null;
    }

    // Extract keyword data from snapshot
    let keywordData = selectedSnapshot.keywords?.find(k => k.keyword === selectedKeyword) ||
                      selectedSnapshot.keywordMetrics?.find(k => k.keyword === selectedKeyword);

    // Fallback to case-insensitive match
    if (!keywordData) {
      keywordData = selectedSnapshot.keywords?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase()) ||
                    selectedSnapshot.keywordMetrics?.find(k => k.keyword?.toLowerCase() === selectedKeyword?.toLowerCase());
    }

    if (!keywordData) {
      return null;
    }

    // Ensure keyword data has testLocations for map display
    return {
      ...keywordData,
      lastAnalyzed: selectedSnapshot.analyzedAt || selectedSnapshot.snapshotDate,
      testLocations: keywordData.testLocations || []
    };
  }, [karteSnapshotDate, selectedKeyword, overview, rankingHistory]);

  // Auto-select latest available snapshot date
  useEffect(() => {
    if (karteAvailableDates.length > 0) {
      const latestDate = karteAvailableDates[karteAvailableDates.length - 1].value;
      setKarteSnapshotDate(latestDate);
    } else {
      // If no snapshots available, reset snapshot date to use overview data
      setKarteSnapshotDate('');
    }
  }, [karteAvailableDates]);

  // Reload overview when a new keyword is selected to ensure fresh data
  useEffect(() => {
    if (selectedKeyword && !rankingHistory.some(entry =>
      (entry.keywords?.some(k => k.keyword === selectedKeyword) ||
       entry.keywordMetrics?.some(k => k.keyword === selectedKeyword))
    )) {
      // If selected keyword is not in ranking history, reload overview to get latest data
      console.log(`🔄 Selected keyword "${selectedKeyword}" not in history, reloading overview...`);
      loadRankingOverview();
    }
  }, [selectedKeyword, rankingHistory, loadRankingOverview]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!locationId || locationId === 'undefined') {
    return (
      <Alert severity="error">
        Ungültige Business-ID. Bitte wählen Sie ein gültiges Business aus der Liste aus.
      </Alert>
    );
  }

  if (!overview) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Ranking-Daten. Stellen Sie sicher, dass Ihr Business-Profil korrekt konfiguriert ist.
      </Alert>
    );
  }


  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
              Lokales Ranking
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {overview?.businessName} • {overview?.location}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1.5}>
            {/* Refresh Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              startIcon={<Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              Aktualisieren
            </Button>

            {/* Right Panel Toggle */}
            <ToggleButtonGroup
              value={rightPanelView}
              exclusive
              onChange={(e, newView) => newView && setRightPanelView(newView)}
              size="small"
            >
              <ToggleButton value="map" sx={{ px: 1.5, py: 0.5 }}>
                <Map sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                <Typography variant="caption" fontWeight={500}>Karte</Typography>
              </ToggleButton>
              <ToggleButton value="timeseries" sx={{ px: 1.5, py: 0.5 }}>
                <Timeline sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                <Typography variant="caption" fontWeight={500}>Zeitverlauf</Typography>
              </ToggleButton>
              <ToggleButton value="comparison" sx={{ px: 1.5, py: 0.5 }}>
                <CompareArrows sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                <Typography variant="caption" fontWeight={500}>Vergleich</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
            
            {/* Refresh All Keywords Button */}
            <Button
              variant="contained"
              startIcon={<Analytics sx={{ fontSize: '1rem' }} />}
              onClick={refreshAllKeywords}
              disabled={analyzing}
              size="small"
              sx={{ px: 1.5, py: 0.75, fontSize: '0.8rem', mr: 1 }}
            >
              Refresh all keywords
            </Button>

            {/* Clear Cache Button */}
            <Button
              variant="outlined"
              startIcon={clearingCache ? (
                <CircularProgress size="1rem" />
              ) : (
                <ClearAll sx={{ fontSize: '1rem' }} />
              )}
              onClick={clearCache}
              disabled={analyzing || clearingCache}
              size="small"
              sx={{ px: 1.5, py: 0.75, fontSize: '0.8rem', mr: 1 }}
            >
              {clearingCache ? 'Clearing...' : 'Cache löschen'}
            </Button>

            {/* Refresh All Volumes Button */}
            <Button
              variant="contained"
              startIcon={refreshingAllVolumes ? (
                <CircularProgress size="1rem" sx={{ color: 'white' }} />
              ) : (
                <BarChart sx={{ fontSize: '1rem' }} />
              )}
              onClick={async () => {
                setRefreshingAllVolumesState(true);
                try {
                  await handleRefreshAllVolumes();
                } finally {
                  setRefreshingAllVolumesState(false);
                }
              }}
              disabled={analyzing || refreshingAllVolumes}
              size="small"
              sx={{
                px: 1.5,
                py: 0.75,
                fontSize: '0.8rem',
                mr: 1,
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              }}
            >
              {refreshingAllVolumes ? 'Berechne...' : 'Refresh all volumes'}
            </Button>

            {/* Simulation Controls 
            <Button
              variant="outlined"
              startIcon={<Analytics sx={{ fontSize: '1rem' }} />}
              endIcon={<MoreVert sx={{ fontSize: '1rem' }} />}
              onClick={(e) => setSimulationMenuAnchor(e.currentTarget)}
              disabled={analyzing}
              size="small"
              sx={{ px: 1.5, py: 0.75, fontSize: '0.8rem' }}
            >
              Simulation
            </Button>

            */}
            
            <Menu
              anchorEl={simulationMenuAnchor}
              open={Boolean(simulationMenuAnchor)}
              onClose={() => setSimulationMenuAnchor(null)}
            >
              <MenuItem onClick={() => { generateSimulation(); setSimulationMenuAnchor(null); }}>
                <Analytics sx={{ mr: 1 }} />
                Daten generieren (5 Wochen)
              </MenuItem>
              <MenuItem onClick={() => { clearSimulation(); setSimulationMenuAnchor(null); }}>
                <Delete sx={{ mr: 1 }} />
                Simulation zurücksetzen
              </MenuItem>
            </Menu>
            
            <Button
              variant="contained"
              startIcon={
                creatingSnapshot ? (
                  <CircularProgress size="1rem" sx={{ color: 'white' }} />
                ) : snapshotSuccess ? (
                  <CheckCircle sx={{ fontSize: '1rem', color: 'success.main' }} />
                ) : (
                  <CameraAlt sx={{ fontSize: '1rem' }} />
                )
              }
              onClick={createSnapshot}
              disabled={analyzing || creatingSnapshot}
              size="small"
              sx={{ 
                px: 2, 
                py: 0.75, 
                fontSize: '0.8rem',
                backgroundColor: snapshotSuccess ? 'success.main' : 'primary.main',
                '&:hover': {
                  backgroundColor: snapshotSuccess ? 'success.dark' : 'primary.dark'
                }
              }}
            >
              {creatingSnapshot ? 'Erstelle...' : snapshotSuccess ? 'Erfolgreich!' : 'Neue Messung'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content - Split Screen Layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Panel - Keywords */}
        <Box 
          sx={{ 
            width: isMobile ? '100%' : '35%',
            borderRight: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            display: isMobile && rightPanelView !== 'keywords' ? 'none' : 'block'
          }}
        >
          <Box sx={{ p: 1, height: '100%' }}>
            <KeywordSidebar
              keywords={overview?.trackedKeywords || []}
              selectedKeyword={selectedKeyword}
              onKeywordSelect={handleKeywordSelect}
              onKeywordDelete={deleteKeyword}
              onKeywordRefresh={handleKeywordRefresh}
              onAddKeyword={() => setKeywordDialog(true)}
              onCreateMeasurement={createSnapshot}
              onCalculateLocalVolume={handleCalculateLocalVolume}
              loading={creatingSnapshot}
              overview={overview}
              locationId={locationId}
              calculatingVolume={calculatingVolume}
            />
          </Box>
        </Box>

        {/* Right Panel - Map or Comparison */}
        <Box 
          sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: isMobile && rightPanelView === 'keywords' ? 'none' : 'block'
          }}
        >
          <Box sx={{ p: 1, height: '100%' }}>
            {rightPanelView === 'map' && (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
                      Ranking Karte
                    </Typography>
                  </Box>

                  {selectedKeyword && (() => {
                    const lastAnalyzed = karteKeywordData?.lastAnalyzed;

                    return lastAnalyzed ? (
                      <Chip
                        label={`Stand: ${new Date(lastAnalyzed).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}`}
                        size="small"
                        variant="outlined"
                        color="default"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    ) : (
                      <Chip
                        label="Keine Daten"
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    );
                  })()}
                </Box>

                {/* Snapshot Date Selector */}
                {selectedKeyword && karteAvailableDates.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Snapshot Datum</InputLabel>
                      <Select
                        value={karteSnapshotDate}
                        onChange={(e) => setKarteSnapshotDate(e.target.value)}
                        label="Snapshot Datum"
                      >
                        {karteAvailableDates.map((date) => (
                          <MenuItem key={date.value} value={date.value}>
                            {date.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {selectedKeyword ? (
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <LocationMap
                      key={`map-${selectedKeyword}-${karteSnapshotDate}`}
                      businessLocation={selectedBusiness}
                      keywords={karteKeywordData ? [karteKeywordData] : []}
                    />
                  </Box>
                ) : (
                  <Card sx={{ flex: 1 }}>
                    <CardContent sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}>
                      <Box>
                        <Map sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          Keyword auswählen
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Wählen Sie ein Keyword aus der Liste, um die Ranking-Positionen auf der Karte zu sehen.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
            
            {rightPanelView === 'timeseries' && (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
                      Ranking-Zeitverlauf
                    </Typography>
                  </Box>
                  
                  {selectedKeyword && rankingHistory.length > 0 && (
                    <Chip
                      label={`${rankingHistory.length} Datenpunkte`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Box>
                
                <RankingTimeSeries
                  overview={overview}
                  selectedKeyword={selectedKeyword}
                  rankingHistory={rankingHistory}
                  onKeywordSelect={setSelectedKeyword}
                />
              </Box>
            )}

            {rightPanelView === 'comparison' && (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0 }}>
                      Ranking-Vergleich
                    </Typography>
                  </Box>
                  
                  {selectedKeyword && rankingHistory.length > 0 && (
                    <Chip
                      label={`${rankingHistory.length} Messungen`}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Box>
                
                <ComparisonMode
                  selectedKeyword={selectedKeyword}
                  rankingHistory={rankingHistory}
                  onCreateMeasurement={createSnapshot}
                  loading={creatingSnapshot}
                  success={snapshotSuccess}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Keyword Dialog */}
      <Dialog
        open={keywordDialog}
        onClose={() => setKeywordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neues Keyword hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              💡 Eigenes Keyword eingeben:
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Keyword"
              fullWidth
              variant="outlined"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder="z.B. Restaurant München, Friseur Berlin, Zahnarzt Hamburg"
              helperText="Geben Sie ein beliebiges Keyword ein oder wählen Sie einen Vorschlag unten"
              sx={{ mb: 1 }}
            />
          </Box>
          
          {keywordSuggestions.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                🤖 Intelligente Keyword-Vorschläge:
              </Typography>
              
              {/* Primary Keywords */}
              {keywordSuggestions.filter(s => s.priority === 'primary').length > 0 && (
                <Box mb={1}>
                  <Typography variant="caption" color="primary" fontWeight="bold">
                    🎯 Priorität: Hoch
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {keywordSuggestions
                      .filter(s => s.priority === 'primary')
                      .slice(0, 4)
                      .map((suggestion, index) => (
                        <Chip
                          key={`primary-${index}`}
                          label={typeof suggestion === 'string' ? suggestion : suggestion.keyword}
                          onClick={() => setNewKeyword(typeof suggestion === 'string' ? suggestion : suggestion.keyword)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                  </Box>
                </Box>
              )}
              
              {/* Secondary Keywords */}
              {keywordSuggestions.filter(s => s.priority === 'secondary').length > 0 && (
                <Box mb={1}>
                  <Typography variant="caption" color="warning.main" fontWeight="bold">
                    📈 Priorität: Mittel
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {keywordSuggestions
                      .filter(s => s.priority === 'secondary')
                      .slice(0, 4)
                      .map((suggestion, index) => (
                        <Chip
                          key={`secondary-${index}`}
                          label={typeof suggestion === 'string' ? suggestion : suggestion.keyword}
                          onClick={() => setNewKeyword(typeof suggestion === 'string' ? suggestion : suggestion.keyword)}
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                  </Box>
                </Box>
              )}
              
              {/* Fallback for simple string array (backwards compatibility) */}
              {keywordSuggestions.length > 0 && 
               typeof keywordSuggestions[0] === 'string' && (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {keywordSuggestions.slice(0, 8).map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => setNewKeyword(suggestion)}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeywordDialog(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={() => analyzeKeyword(newKeyword)}
            variant="contained"
            disabled={!newKeyword.trim() || analyzing}
          >
            {analyzing ? <CircularProgress size={20} /> : 'Analysieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Overlay */}
      <RankingProgressOverlay
        open={analyzing}
        onClose={() => {
          if (progressData?.phase === 'completed') {
            setAnalyzing(false);
            setProgressData(null);
          }
        }}
        progressData={progressData}
        keyword={progressKeyword}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.type}
          variant="filled"
          sx={{ width: '100%', minWidth: '300px', color: 'white' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default RankingDashboard;